import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { CameraScan, hasDetector } from '../components/CameraScan.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { QrCode } from '../components/Receipt.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone, TableWrap } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day as dayText, money, parseMoney, since } from '../lib/format.ts';
import { shrinkPhoto } from '../lib/images.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi } from './Reports.tsx';
import { slugify } from './Website.tsx';

/**
 * Events: the business prepares events with their kinds of tickets, puts them on its public events
 * page, books people at the desk or lets them book online, keeps a waiting list, and checks people
 * in at the door by the QR code on their ticket.
 */

type EState = 'draft' | 'published' | 'cancelled';
type Phase = 'draft' | 'cancelled' | 'over' | 'running' | 'upcoming';
type Booking = 'open' | 'waitlist' | 'full' | 'closed' | 'over' | 'cancelled' | 'draft';
type RState = 'confirmed' | 'waiting' | 'cancelled';
type Payment = 'free' | 'to_pay' | 'paid';
type Method = 'cash' | 'card' | 'transfer';
type When = 'upcoming' | 'past' | 'draft' | 'cancelled' | 'all';
type Filter = 'all' | 'confirmed' | 'checked_in' | 'to_pay' | 'waiting' | 'cancelled';
type View = 'people' | 'door' | 'details';

interface Counts {
  confirmed: number;
  waiting: number;
  cancelled: number;
  checked_in: number;
  paid: number;
  to_pay: number;
}
interface ListRow extends Counts {
  id: string;
  number: number;
  slug: string;
  name: string;
  summary: string | null;
  venue: string | null;
  online: boolean;
  starts: string;
  ends: string;
  day: string;
  time: string;
  end_day: string;
  end_time: string;
  state: EState;
  phase: Phase;
  capacity: number | null;
  cover: string | null;
  cover_version: string | null;
  tags: string[];
}
interface Can {
  edit: boolean;
  publish: boolean;
  register: boolean;
  check_in: boolean;
  settings?: boolean;
}
interface Overview {
  settings: { public: boolean; intro: string | null };
  today: string;
  counts: { upcoming: number; drafts: number; booked_30: number; online_30: number; seats: number; filled: number; to_pay: number; to_pay_n: number; waiting: number };
  today_events: ListRow[];
  next: ListRow[];
  can: Can & { settings: boolean };
}
interface TicketKind {
  id: string;
  name: string;
  description: string | null;
  price: number;
  seats: number | null;
  sequence: number;
  taken: number;
  left: number | null;
}
interface EventFull {
  id: string;
  number: number;
  slug: string;
  name: string;
  summary: string | null;
  description: string | null;
  cover: string | null;
  cover_version: string | null;
  venue: string | null;
  address: string | null;
  online_url: string | null;
  starts: string;
  ends: string;
  starts_local: string;
  ends_local: string;
  closes_local: string | null;
  state: EState;
  phase: Phase;
  booking: Booking;
  capacity: number | null;
  waitlist: boolean;
  registration_open: boolean;
  contact_email: string | null;
  contact_phone: string | null;
  tags: string[];
  cancel_reason: string | null;
  published_at: string | null;
  tickets: TicketKind[];
  counts: Counts;
  can: Can;
}
interface Reg {
  id: string;
  ref: string;
  ticket: string;
  ticket_name: string | null;
  booking: string;
  name: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  note: string | null;
  state: RState;
  price: number;
  payment: Payment;
  paid_method: Method | null;
  paid_at: string | null;
  paid_by_name: string | null;
  source: 'web' | 'staff';
  code: string;
  token: string;
  checked_in_at: string | null;
  checked_in_name: string | null;
  created_at: string;
}
type Scan = { result: 'ok' | 'already' | 'waiting' | 'cancelled' | 'other_event' | 'unknown'; registration: Reg | null; other_event?: string };

const PHASE: Record<Phase, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  cancelled: { tone: 'danger', icon: 'x' },
  over: { tone: 'neutral', icon: 'checkCircle' },
  running: { tone: 'ok', icon: 'dot' },
  upcoming: { tone: 'info', icon: 'calendar' },
};
const R_STATE: Record<RState, { tone: Tone; icon: IconName }> = {
  confirmed: { tone: 'ok', icon: 'checkCircle' },
  waiting: { tone: 'warn', icon: 'clock' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const METHODS: Method[] = ['cash', 'card', 'transfer'];
const WHEN: When[] = ['upcoming', 'past', 'draft', 'cancelled', 'all'];
const FILTERS: Filter[] = ['all', 'confirmed', 'checked_in', 'to_pay', 'waiting', 'cancelled'];
const MAX_IMAGE = 600_000;

const publicUrl = (tenant: string, path = '') => `${window.location.origin}/events/${tenant}${path}`;
const coverUrl = (tenant: string, id: string, v: string | null) => `/media/${encodeURIComponent(tenant)}/event/${id}${v ? `?v=${v}` : ''}`;
const when = (e: { day: string; time: string; end_day: string; end_time: string }) =>
  e.day === e.end_day
    ? t('ev.when_same_day', { day: dayText(e.day), from: e.time, to: e.end_time })
    : t('ev.when_days', { day: dayText(e.day), from: e.time, end_day: dayText(e.end_day), to: e.end_time });
const priceText = (minor: number) => (minor === 0 ? t('ev.free') : money(minor));

function PhaseBadge({ phase }: { phase: Phase }) {
  return <StatusBadge tone={PHASE[phase].tone} icon={PHASE[phase].icon} label={t(`ev.phase_${phase}` as MessageKey)} pulse={phase === 'running'} />;
}
function RegBadge({ r }: { r: Reg }) {
  if (r.state === 'confirmed' && r.checked_in_at) return <StatusBadge tone="primary" icon="badgeCheck" label={t('ev.r_checked_in')} />;
  return <StatusBadge tone={R_STATE[r.state].tone} icon={R_STATE[r.state].icon} label={t(`ev.r_${r.state}` as MessageKey)} />;
}
function PayBadge({ r }: { r: Reg }) {
  if (r.payment === 'free') return <span className="muted small">{t('ev.free')}</span>;
  if (r.payment === 'paid') return <StatusBadge tone="ok" icon="cash" label={t('ev.paid_by', { method: t(`ev.m_${r.paid_method ?? 'cash'}` as MessageKey) })} />;
  return <StatusBadge tone="warn" icon="cash" label={t('ev.to_pay_amount', { amount: money(r.price) })} />;
}

/** Seats taken out of the event's limit, as a bar. */
function Fill({ taken, capacity, waiting }: { taken: number; capacity: number | null; waiting?: number }) {
  if (capacity === null) {
    return <span className="small muted">{tn('ev.n_booked', taken)}</span>;
  }
  const pct = Math.min(100, Math.round((taken / Math.max(1, capacity)) * 100));
  return (
    <span className="ev-fill">
      <span className="ev-fill-bar" aria-hidden="true">
        <span className={`ev-fill-in${pct >= 100 ? ' full' : pct >= 80 ? ' high' : ''}`} style={{ width: `${pct}%` }} />
      </span>
      <span className="small">
        {t('ev.taken_of', { taken, capacity })}
        {waiting ? <span className="ev-waiting"> · {tn('ev.n_waiting', waiting)}</span> : null}
      </span>
    </span>
  );
}

export function Events() {
  const [params, setParams] = useSearchParams();
  const { data: o, reload } = useAction<Overview>('events.overview');
  const { tenant } = useSession();
  const slug = tenant?.slug ?? '';
  const [settings, setSettings] = useState(false);
  const eventId = params.get('event');
  const set = (patch: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p);
  };
  const tab = params.get('tab') === 'list' ? 'list' : 'overview';
  return (
    <Shell>
      <div className="page reports ev-page">
        {eventId ? (
          <EventPage key={eventId} id={eventId} slug={slug} view={(params.get('view') as View | null) ?? 'people'} set={set} onChange={() => void reload()} />
        ) : (
          <>
            <div className="page-head">
              <div>
                <h1>{t('ev.title')}</h1>
                <p className="muted">{t('ev.subtitle')}</p>
              </div>
              <div className="page-actions">
                {o?.settings.public ? (
                  <a className="btn btn-ghost btn-md" href={publicUrl(slug)} target="_blank" rel="noopener noreferrer">
                    <Icon name="external" size={20} />
                    <span className="btn-label">{t('ev.public_page')}</span>
                  </a>
                ) : null}
                {o?.can.settings ? <IconButton icon="settings" label={t('ev.settings')} onClick={() => setSettings(true)} /> : null}
                {o?.can.edit ? (
                  <Button variant="primary" icon="plus" onClick={() => set({ event: 'new', view: 'details' })}>
                    {t('ev.new_event')}
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="segmented tabs" role="tablist" aria-label={t('ev.sections')}>
              {(['overview', 'list'] as const).map((k) => (
                <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => set({ tab: k === 'overview' ? null : k })}>
                  <Icon name={k === 'overview' ? 'chart' : 'list'} size={16} />
                  {t(`ev.tab_${k}` as MessageKey)}
                </button>
              ))}
            </div>
            {!o ? (
              <Skeleton h={300} r="var(--radius-lg)" />
            ) : tab === 'overview' ? (
              <OverviewTab o={o} slug={slug} open={(id, view) => set({ event: id, view: view ?? null })} onSettings={() => setSettings(true)} />
            ) : (
              <ListTab slug={slug} open={(id) => set({ event: id, view: null })} />
            )}
          </>
        )}
      </div>
      {settings && o ? (
        <SettingsDialog
          initial={o.settings}
          slug={slug}
          onClose={() => setSettings(false)}
          onSaved={() => {
            setSettings(false);
            void reload();
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- overview ----------------------------------------------------------------------------

function EventLine({ e, slug, onOpen, action: extra }: { e: ListRow; slug: string; onOpen: () => void; action?: ReactNode }) {
  return (
    <li className="ev-line">
      <button type="button" className="ev-line-main" onClick={onOpen}>
        {e.cover ? (
          <img className="ev-thumb" src={coverUrl(slug, e.cover, e.cover_version)} alt="" loading="lazy" />
        ) : (
          <span className="ev-thumb ev-thumb-empty" aria-hidden="true">
            <Icon name="ticket" size={20} />
          </span>
        )}
        <span className="ev-line-text">
          <strong>{e.name}</strong>
          <span className="muted small">
            {when(e)}
            {e.venue ? ` · ${e.venue}` : e.online ? ` · ${t('ev.online')}` : ''}
          </span>
          <Fill taken={e.confirmed} capacity={e.capacity} waiting={e.waiting} />
        </span>
        <span className="ev-line-end">
          <PhaseBadge phase={e.phase} />
        </span>
      </button>
      {extra}
    </li>
  );
}

function OverviewTab({ o, slug, open, onSettings }: { o: Overview; slug: string; open: (id: string, view?: View) => void; onSettings: () => void }) {
  const c = o.counts;
  const pct = c.seats ? Math.round((c.filled / c.seats) * 100) : null;
  return (
    <div className="ev-overview">
      {!o.settings.public && o.can.settings ? (
        <p className="callout ev-callout">
          <Icon name="globe" size={18} />
          <span>{t('ev.page_off')}</span>
          <Button size="sm" variant="secondary" onClick={onSettings}>
            {t('ev.open_page')}
          </Button>
        </p>
      ) : null}
      <section className="rep-kpis" aria-label={t('ev.glance')}>
        <Kpi label={t('ev.k_upcoming')} value={String(c.upcoming)} hint={c.drafts ? tn('ev.n_drafts', c.drafts) : undefined} />
        <Kpi label={t('ev.k_booked')} value={String(c.booked_30)} hint={t('ev.k_booked_hint', { n: c.online_30 })} />
        <Kpi label={t('ev.k_filled')} value={pct === null ? '—' : `${pct}%`} hint={c.seats ? t('ev.taken_of', { taken: c.filled, capacity: c.seats }) : t('ev.k_filled_none')} />
        <Kpi label={t('ev.k_to_pay')} value={money(c.to_pay)} hint={c.to_pay_n ? tn('ev.n_people', c.to_pay_n) : undefined} warn={c.to_pay_n > 0} />
        <Kpi label={t('ev.k_waiting')} value={String(c.waiting)} hint={c.waiting ? t('ev.k_waiting_hint') : undefined} warn={c.waiting > 0} />
      </section>
      <div className="ev-columns">
        <section className="panel">
          <div className="panel-head">
            <h2>{t('ev.today')}</h2>
            <span className="muted small">{dayText(o.today)}</span>
          </div>
          {o.today_events.length ? (
            <ul className="ev-lines">
              {o.today_events.map((e) => (
                <EventLine
                  key={e.id}
                  e={e}
                  slug={slug}
                  onOpen={() => open(e.id)}
                  action={
                    o.can.check_in ? (
                      <Button variant="primary" size="sm" icon="scan" onClick={() => open(e.id, 'door')}>
                        {t('ev.door')}
                      </Button>
                    ) : null
                  }
                />
              ))}
            </ul>
          ) : (
            <EmptyState icon="calendar" title={t('ev.nothing_today')} body={t('ev.nothing_today_body')} />
          )}
        </section>
        <section className="panel">
          <div className="panel-head">
            <h2>{t('ev.coming_up')}</h2>
          </div>
          {o.next.length ? (
            <ul className="ev-lines">
              {o.next.map((e) => (
                <EventLine key={e.id} e={e} slug={slug} onOpen={() => open(e.id)} />
              ))}
            </ul>
          ) : (
            <EmptyState icon="ticket" title={t('ev.none_coming')} body={o.can.edit ? t('ev.none_coming_edit') : t('ev.none_coming_body')} />
          )}
        </section>
      </div>
    </div>
  );
}

function ListTab({ slug, open }: { slug: string; open: (id: string) => void }) {
  const [whenF, setWhen] = useState<When>('upcoming');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);
  const { data } = useAction<ListRow[]>('events.list', { when: whenF, q: query || null });
  return (
    <section className="panel">
      <div className="ev-filters">
        <div className="segmented" role="tablist" aria-label={t('ev.which')}>
          {WHEN.map((w) => (
            <button key={w} role="tab" aria-selected={whenF === w} className={whenF === w ? 'active' : ''} onClick={() => setWhen(w)}>
              {t(`ev.when_${w}` as MessageKey)}
            </button>
          ))}
        </div>
        <input className="input ev-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ev.search_events')} aria-label={t('ev.search_events')} />
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : data.length ? (
        <ul className="ev-lines">
          {data.map((e) => (
            <EventLine key={e.id} e={e} slug={slug} onOpen={() => open(e.id)} />
          ))}
        </ul>
      ) : (
        <EmptyState icon="search" title={t('ev.no_events')} body={query ? t('ev.no_events_q') : t(`ev.no_events_${whenF}` as MessageKey)} />
      )}
    </section>
  );
}

// ---- one event ---------------------------------------------------------------------------

function EventPage({ id, slug, view, set, onChange }: { id: string; slug: string; view: View; set: (p: Record<string, string | null>) => void; onChange: () => void }) {
  const isNew = id === 'new';
  const { fail } = useUi();
  const [e, setE] = useState<EventFull | null>(null);
  const [version, setVersion] = useState(0);
  const load = () => {
    if (isNew) return Promise.resolve();
    return action<EventFull>('events.get', { id }).then(setE, fail);
  };
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const changed = async () => {
    setVersion((v) => v + 1);
    await load();
    onChange();
  };
  const back = () => set({ event: null, view: null });
  if (isNew) {
    return (
      <>
        <PageTop title={t('ev.new_event')} back={back} />
        <Editor slug={slug} event={null} onSaved={(newId) => set({ event: newId, view: 'people' })} onCancel={back} />
      </>
    );
  }
  if (!e) return <Skeleton h={320} r="var(--radius-lg)" />;
  const views: { key: View; icon: IconName; show: boolean; badge?: number }[] = [
    { key: 'people', icon: 'users', show: true, badge: e.counts.waiting || undefined },
    { key: 'door', icon: 'scan', show: e.can.check_in && e.state === 'published' },
    { key: 'details', icon: 'edit', show: true },
  ];
  const v = views.find((x) => x.key === view && x.show)?.key ?? 'people';
  return (
    <>
      <PageTop title={e.name} back={back} sub={<EventSub e={e} />}>
        <EventActions
          e={e}
          slug={slug}
          onChange={changed}
          onGone={() => {
            back();
            onChange();
          }}
          onCopy={(newId) => set({ event: newId, view: 'details' })}
        />
      </PageTop>
      {e.state === 'cancelled' ? (
        <p className="callout callout-danger ev-callout">
          <Icon name="alert" size={18} />
          <span>
            <strong>{t('ev.was_cancelled')}</strong> {e.cancel_reason}
          </span>
        </p>
      ) : e.state === 'draft' ? (
        <p className="callout ev-callout">
          <Icon name="eyeOff" size={18} />
          <span>{t('ev.draft_note')}</span>
        </p>
      ) : null}
      <section className="rep-kpis ev-kpis" aria-label={t('ev.glance')}>
        <Kpi
          label={t('ev.k_confirmed')}
          value={e.capacity === null ? String(e.counts.confirmed) : `${e.counts.confirmed} / ${e.capacity}`}
          hint={e.capacity !== null ? tn('ev.n_free', Math.max(0, e.capacity - e.counts.confirmed)) : t('ev.no_limit')}
        />
        <Kpi label={t('ev.k_checked_in')} value={String(e.counts.checked_in)} hint={e.counts.confirmed ? t('ev.of_confirmed', { n: e.counts.confirmed }) : undefined} />
        <Kpi label={t('ev.k_waiting')} value={String(e.counts.waiting)} hint={e.waitlist ? t('ev.waitlist_on') : t('ev.waitlist_off')} />
        <Kpi label={t('ev.k_to_pay')} value={money(e.counts.to_pay)} warn={e.counts.to_pay > 0} hint={t('ev.paid_amount', { amount: money(e.counts.paid) })} />
      </section>
      <div className="segmented tabs" role="tablist" aria-label={t('ev.sections')}>
        {views
          .filter((x) => x.show)
          .map((x) => (
            <button key={x.key} role="tab" aria-selected={v === x.key} className={v === x.key ? 'active' : ''} onClick={() => set({ view: x.key === 'people' ? null : x.key })}>
              <Icon name={x.icon} size={16} />
              {t(`ev.view_${x.key}` as MessageKey)}
              {x.badge ? <span className="count">{x.badge}</span> : null}
            </button>
          ))}
      </div>
      {v === 'people' ? (
        <People key={version} e={e} slug={slug} onChange={changed} />
      ) : v === 'door' ? (
        <Door e={e} onChange={changed} />
      ) : e.can.edit ? (
        <Editor slug={slug} event={e} onSaved={() => void changed()} onCancel={() => set({ view: null })} />
      ) : (
        <Details e={e} />
      )}
    </>
  );
}

function PageTop({ title, back, sub, children }: { title: string; back: () => void; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-head ev-head">
      <div className="ev-head-main">
        <button type="button" className="link-button ev-back" onClick={back}>
          <Icon name="back" size={16} /> {t('ev.all_events')}
        </button>
        <h1>{title}</h1>
        {sub}
      </div>
      {children ? <div className="page-actions">{children}</div> : null}
    </div>
  );
}

function EventSub({ e }: { e: EventFull }) {
  const local = (s: string) => ({ day: s.slice(0, 10), time: s.slice(11, 16) });
  const a = local(e.starts_local);
  const b = local(e.ends_local);
  return (
    <div className="ev-sub">
      <PhaseBadge phase={e.phase} />
      <span className="muted">
        <Icon name="calendar" size={16} /> {when({ day: a.day, time: a.time, end_day: b.day, end_time: b.time })}
      </span>
      {e.venue ? (
        <span className="muted">
          <Icon name="pin" size={16} /> {e.venue}
        </span>
      ) : null}
      {e.state === 'published' && e.booking !== 'open' ? <span className="ev-chip">{t(`ev.b_${e.booking}` as MessageKey)}</span> : null}
    </div>
  );
}

function EventActions({ e, slug, onChange, onGone, onCopy }: { e: EventFull; slug: string; onChange: () => Promise<void>; onGone: () => void; onCopy: (id: string) => void }) {
  const { busy, run } = useSave();
  const [ask, setAsk] = useState<'cancel' | 'remove' | 'unpublish' | null>(null);
  const [reason, setReason] = useState('');
  const publish = (published: boolean) => run(() => action('events.publish', { id: e.id, published }).then(onChange), published ? t('ev.published') : t('ev.unpublished'));
  const people = e.counts.confirmed + e.counts.waiting;
  return (
    <>
      {e.state === 'published' ? (
        <a className="btn btn-ghost btn-md" href={publicUrl(slug, `/${e.slug}`)} target="_blank" rel="noopener noreferrer">
          <Icon name="external" size={20} />
          <span className="btn-label">{t('ev.see_public')}</span>
        </a>
      ) : null}
      {e.can.edit ? (
        <Button
          variant="ghost"
          icon="copy"
          busy={busy}
          onClick={() =>
            void run(async () => {
              const r = await action<{ id: string }>('events.duplicate', { id: e.id });
              onCopy(r.id);
            }, t('ev.copied'))
          }
        >
          {t('ev.duplicate')}
        </Button>
      ) : null}
      {e.can.publish && e.state === 'draft' ? (
        <Button variant="primary" icon="globe" busy={busy} onClick={() => void publish(true)}>
          {t('ev.publish')}
        </Button>
      ) : null}
      {e.can.publish && e.state === 'published' && e.phase !== 'over' ? (
        <>
          {!people ? (
            <Button variant="ghost" icon="eyeOff" onClick={() => setAsk('unpublish')}>
              {t('ev.unpublish')}
            </Button>
          ) : null}
          <Button variant="ghost" icon="x" onClick={() => setAsk('cancel')}>
            {t('ev.cancel_event')}
          </Button>
        </>
      ) : null}
      {e.can.publish && e.state !== 'published' && !people && !e.counts.cancelled ? <IconButton icon="trash" label={t('ev.remove')} onClick={() => setAsk('remove')} /> : null}
      {ask === 'cancel' ? (
        <Dialog
          title={t('ev.cancel_q', { name: e.name })}
          onClose={() => setAsk(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setAsk(null)}>
                {t('ev.keep_event')}
              </Button>
              <Button
                variant="danger"
                icon="x"
                busy={busy}
                disabled={reason.trim().length < 3}
                onClick={() =>
                  void run(async () => {
                    const r = await action<{ cancelled: number; paid: number }>('events.cancel', { id: e.id, reason: reason.trim() });
                    setAsk(null);
                    await onChange();
                    return r;
                  }, t('ev.event_cancelled'))
                }
              >
                {t('ev.cancel_event')}
              </Button>
            </>
          }
        >
          <p>{people ? tn('ev.cancel_body', people) : t('ev.cancel_body_none')}</p>
          {e.counts.paid ? (
            <p className="callout callout-warn">
              <Icon name="cash" size={18} /> {t('ev.cancel_paid', { amount: money(e.counts.paid) })}
            </p>
          ) : null}
          <Field label={t('ev.cancel_reason')} required hint={t('ev.cancel_reason_hint')}>
            <textarea className="input textarea" rows={3} value={reason} onChange={(x) => setReason(x.target.value)} maxLength={300} />
          </Field>
        </Dialog>
      ) : null}
      {ask === 'remove' ? (
        <Confirm
          title={t('ev.remove_q', { name: e.name })}
          body={t('ev.remove_body')}
          confirm={t('ev.remove')}
          danger
          onClose={() => setAsk(null)}
          onConfirm={() => void run(() => action('events.remove', { id: e.id }).then(onGone), t('ev.removed'))}
        />
      ) : null}
      {ask === 'unpublish' ? (
        <Confirm
          title={t('ev.unpublish_q')}
          body={t('ev.unpublish_body')}
          confirm={t('ev.unpublish')}
          onClose={() => setAsk(null)}
          onConfirm={() => {
            setAsk(null);
            void publish(false);
          }}
        />
      ) : null}
    </>
  );
}

// ---- the people booked -------------------------------------------------------------------

function People({ e, slug, onChange }: { e: EventFull; slug: string; onChange: () => Promise<void> }) {
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<Reg | null>(null);
  const [paying, setPaying] = useState(false);
  const [dropping, setDropping] = useState(false);
  const { busy, run } = useSave();
  useEffect(() => {
    const h = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);
  const { data, reload } = useAction<Reg[]>('events.registrations', { event: e.id, state: filter, q: query || null });
  const rows = data ?? [];
  const sel = rows.filter((r) => picked.has(r.id));
  const changed = async () => {
    setPicked(new Set());
    await reload();
    await onChange();
  };
  const toggle = (id: string) => {
    const n = new Set(picked);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setPicked(n);
  };
  const live = e.state !== 'cancelled';
  const canPay = sel.some((r) => r.state === 'confirmed' && r.payment === 'to_pay');
  const canConfirm = sel.some((r) => r.state === 'waiting');
  const canCancel = sel.some((r) => r.state !== 'cancelled');
  const exportCsv = () =>
    downloadCsv(
      `${e.slug}-${t('ev.csv_name')}`,
      [t('ev.ref'), t('ev.f_name'), t('ev.f_email'), t('ev.f_phone'), t('ev.f_company'), t('ev.ticket_kind'), t('ev.state'), t('ev.price'), t('ev.payment'), t('ev.checked_in')],
      rows.map((r) => [
        r.ref,
        r.name,
        r.email ?? '',
        r.phone ?? '',
        r.company ?? '',
        r.ticket_name ?? '',
        t(`ev.r_${r.state}` as MessageKey),
        r.price / 100,
        t(`ev.p_${r.payment}` as MessageKey),
        r.checked_in_at ? dateTime(r.checked_in_at) : '',
      ]),
    );
  return (
    <section className="panel ev-people">
      <div className="ev-filters">
        <div className="segmented ev-filter-tabs" role="tablist" aria-label={t('ev.show')}>
          {FILTERS.map((f) => (
            <button key={f} role="tab" aria-selected={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
              {t(`ev.f_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        <div className="ev-filter-end">
          <input className="input ev-search" type="search" value={q} onChange={(x) => setQ(x.target.value)} placeholder={t('ev.search_people')} aria-label={t('ev.search_people')} />
          {rows.length ? <IconButton icon="download" label={t('ev.export')} onClick={exportCsv} /> : null}
          {e.can.register && live ? (
            <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
              {t('ev.add_booking')}
            </Button>
          ) : null}
        </div>
      </div>
      {sel.length ? (
        <div className="ev-bulk" role="region" aria-label={t('ev.selected_actions')}>
          <strong>{tn('ev.n_selected', sel.length)}</strong>
          {e.can.register && canPay ? (
            <Button size="sm" variant="secondary" icon="cash" onClick={() => setPaying(true)}>
              {t('ev.mark_paid')}
            </Button>
          ) : null}
          {e.can.register && canConfirm && live ? (
            <Button
              size="sm"
              variant="secondary"
              icon="checkCircle"
              busy={busy}
              onClick={() => void run(() => action('events.confirm', { ids: sel.filter((r) => r.state === 'waiting').map((r) => r.id) }).then(changed), t('ev.confirmed_done'))}
            >
              {t('ev.give_seat')}
            </Button>
          ) : null}
          {e.can.register && canCancel ? (
            <Button size="sm" variant="ghost" icon="x" onClick={() => setDropping(true)}>
              {t('ev.cancel_bookings')}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())}>
            {t('ev.clear_selection')}
          </Button>
        </div>
      ) : null}
      {!data ? (
        <Skeleton h={240} />
      ) : !rows.length ? (
        <EmptyState
          icon="users"
          title={query || filter !== 'all' ? t('ev.no_people_q') : t('ev.no_people')}
          body={query || filter !== 'all' ? undefined : e.state === 'published' ? t('ev.no_people_body') : t('ev.no_people_draft')}
        />
      ) : (
        <TableWrap>
          <table className="data-table ev-table">
            <thead>
              <tr>
                <th className="ev-check">
                  <input
                    type="checkbox"
                    aria-label={t('ev.select_all')}
                    checked={sel.length === rows.length}
                    ref={(el) => {
                      if (el) el.indeterminate = sel.length > 0 && sel.length < rows.length;
                    }}
                    onChange={() => setPicked(sel.length === rows.length ? new Set() : new Set(rows.map((r) => r.id)))}
                  />
                </th>
                <th className="left">{t('ev.f_name')}</th>
                <th className="left hide-phone">{t('ev.ticket_kind')}</th>
                <th className="left">{t('ev.state')}</th>
                <th className="left hide-phone">{t('ev.payment')}</th>
                <th className="hide-phone">{t('ev.booked_at')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`clickable${r.state === 'cancelled' ? ' muted-row' : ''}`} onClick={() => setOpen(r)}>
                  <td className="ev-check" onClick={(x) => x.stopPropagation()}>
                    <input type="checkbox" aria-label={t('ev.select_one', { name: r.name })} checked={picked.has(r.id)} onChange={() => toggle(r.id)} />
                  </td>
                  <td className="left">
                    <button type="button" className="link-button ev-name" onClick={(x) => (x.stopPropagation(), setOpen(r))}>
                      {r.name}
                    </button>
                    <span className="muted small ev-name-sub">
                      {[r.company, r.email].filter(Boolean).join(' · ')}
                      {r.source === 'web' ? (
                        <span className="ev-src" title={t('ev.booked_online')}>
                          <Icon name="globe" size={12} /> <span className="sr-only">{t('ev.booked_online')}</span>
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className="left hide-phone">{r.ticket_name}</td>
                  <td className="left">
                    <RegBadge r={r} />
                  </td>
                  <td className="left hide-phone">
                    <PayBadge r={r} />
                  </td>
                  <td className="hide-phone muted small">{since(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
      {adding ? (
        <AddBooking
          e={e}
          onClose={() => setAdding(false)}
          onDone={async () => {
            setAdding(false);
            await changed();
          }}
        />
      ) : null}
      {open ? (
        <RegDialog
          e={e}
          slug={slug}
          r={open}
          onClose={() => setOpen(null)}
          onChange={async () => {
            setOpen(null);
            await changed();
          }}
        />
      ) : null}
      {paying ? (
        <PayDialog
          regs={sel.filter((r) => r.state === 'confirmed' && r.payment === 'to_pay')}
          onClose={() => setPaying(false)}
          onDone={async () => {
            setPaying(false);
            await changed();
          }}
        />
      ) : null}
      {dropping ? (
        <Confirm
          title={tn('ev.cancel_n_q', sel.filter((r) => r.state !== 'cancelled').length)}
          body={sel.some((r) => r.payment === 'paid' && r.state !== 'cancelled') ? t('ev.cancel_n_paid') : t('ev.cancel_n_body')}
          confirm={t('ev.cancel_bookings')}
          danger
          onClose={() => setDropping(false)}
          onConfirm={() =>
            void run(async () => {
              await action('events.cancel_registration', { ids: sel.filter((r) => r.state !== 'cancelled').map((r) => r.id) });
              setDropping(false);
              await changed();
            }, t('ev.bookings_cancelled'))
          }
        />
      ) : null}
    </section>
  );
}

function PayDialog({ regs, onClose, onDone }: { regs: Reg[]; onClose: () => void; onDone: () => Promise<void> }) {
  const [method, setMethod] = useState<Method>('cash');
  const { busy, run } = useSave();
  const total = regs.reduce((s, r) => s + r.price, 0);
  return (
    <Dialog
      title={tn('ev.pay_q', regs.length)}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="cash" busy={busy} onClick={() => void run(() => action('events.pay', { ids: regs.map((r) => r.id), method }).then(onDone), t('ev.paid_done'))}>
            {t('ev.mark_paid_amount', { amount: money(total) })}
          </Button>
        </>
      }
    >
      <p className="ev-pay-total">
        <span className="muted">{t('ev.total')}</span> <strong>{money(total)}</strong>
      </p>
      <MethodPick value={method} onChange={setMethod} />
      <p className="muted small">{t('ev.pay_note')}</p>
    </Dialog>
  );
}

function MethodPick({ value, onChange }: { value: Method; onChange: (m: Method) => void }) {
  return (
    <div className="segmented ev-methods" role="radiogroup" aria-label={t('ev.method')}>
      {METHODS.map((m) => (
        <button key={m} type="button" role="radio" aria-checked={value === m} className={value === m ? 'active' : ''} onClick={() => onChange(m)}>
          <Icon name={m === 'cash' ? 'cash' : m === 'card' ? 'card' : 'bank'} size={16} />
          {t(`ev.m_${m}` as MessageKey)}
        </button>
      ))}
    </div>
  );
}

function AddBooking({ e, onClose, onDone }: { e: EventFull; onClose: () => void; onDone: () => Promise<void> }) {
  const [ticket, setTicket] = useState(() => e.tickets.find((x) => x.left === null || x.left > 0)?.id ?? e.tickets[0]?.id ?? '');
  const [names, setNames] = useState<string[]>(['']);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [note, setNote] = useState('');
  const [paidNow, setPaidNow] = useState(false);
  const [method, setMethod] = useState<Method>('cash');
  const { busy, run } = useSave();
  const kind = e.tickets.find((x) => x.id === ticket);
  const waits = e.booking === 'waitlist' || e.booking === 'full' || (kind?.left !== null && kind !== undefined && kind.left < names.length);
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    void run(
      async () => {
        const r = await action<{ state: RState }>('events.register', {
          event: e.id,
          ticket,
          attendees: names.map((name) => ({ name: name.trim() })),
          email: email.trim() || null,
          phone: phone.trim() || null,
          company: company.trim() || null,
          note: note.trim() || null,
          paid: paidNow && kind && kind.price > 0 ? method : null,
        });
        await onDone();
        return r;
      },
      waits ? t('ev.added_waiting') : t('ev.added'),
    );
  };
  return (
    <Dialog
      title={t('ev.add_booking')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon={waits ? 'clock' : 'ticket'} busy={busy} type="submit" form="ev-add">
            {waits ? t('ev.add_to_waitlist') : tn('ev.book_n', names.length)}
          </Button>
        </>
      }
    >
      <form id="ev-add" className="form-stack" onSubmit={submit}>
        <Field label={t('ev.ticket_kind')}>
          <select className="input" value={ticket} onChange={(x) => setTicket(x.target.value)}>
            {e.tickets.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name} · {priceText(k.price)}
                {k.left !== null ? ` · ${tn('ev.n_free', k.left)}` : ''}
              </option>
            ))}
          </select>
        </Field>
        {names.map((n, i) => (
          <div key={i} className="ev-guest">
            <Field label={i === 0 ? t('ev.f_name') : t('ev.guest_n', { n: i + 1 })} required>
              <input className="input" value={n} onChange={(x) => setNames(names.map((y, j) => (j === i ? x.target.value : y)))} maxLength={80} autoFocus={i === names.length - 1} />
            </Field>
            {i > 0 ? <IconButton icon="x" label={t('ev.remove_guest', { n: i + 1 })} onClick={() => setNames(names.filter((_, j) => j !== i))} /> : null}
          </div>
        ))}
        {names.length < 10 ? (
          <Button size="sm" variant="ghost" icon="plus" onClick={() => setNames([...names, ''])}>
            {t('ev.add_person')}
          </Button>
        ) : null}
        <div className="ev-two">
          <Field label={t('ev.f_email')} hint={t('ev.f_email_hint')}>
            <input className="input" type="email" value={email} onChange={(x) => setEmail(x.target.value)} maxLength={160} />
          </Field>
          <Field label={t('ev.f_phone')}>
            <input className="input" type="tel" value={phone} onChange={(x) => setPhone(x.target.value)} maxLength={40} />
          </Field>
        </div>
        <Field label={t('ev.f_company')}>
          <input className="input" value={company} onChange={(x) => setCompany(x.target.value)} maxLength={120} />
        </Field>
        <Field label={t('ev.f_note')}>
          <textarea className="input textarea" rows={2} value={note} onChange={(x) => setNote(x.target.value)} maxLength={600} />
        </Field>
        {kind && kind.price > 0 && !waits ? (
          <div className="ev-paynow">
            <div className="setting-row ev-paid-row">
              <strong>{t('ev.paid_now', { amount: money(kind.price * names.length) })}</strong>
              <Switch checked={paidNow} onChange={setPaidNow} label={t('ev.paid_now', { amount: money(kind.price * names.length) })} />
            </div>
            {paidNow ? <MethodPick value={method} onChange={setMethod} /> : null}
          </div>
        ) : null}
        {waits ? (
          <p className="callout callout-warn">
            <Icon name="clock" size={18} /> {t('ev.will_wait')}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

function RegDialog({ e, slug, r, onClose, onChange }: { e: EventFull; slug: string; r: Reg; onClose: () => void; onChange: () => Promise<void> }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ name: r.name, email: r.email ?? '', phone: r.phone ?? '', company: r.company ?? '', note: r.note ?? '' });
  const [qr, setQr] = useState<string[] | null>(null);
  const [paying, setPaying] = useState(false);
  const [method, setMethod] = useState<Method>('cash');
  const { busy, run } = useSave();
  const { fail, toast } = useUi();
  useEffect(() => {
    if (r.state === 'confirmed') action<{ rows: string[] }>('events.ticket_qr', { id: r.id }).then((x) => setQr(x.rows), fail);
  }, [r.id, r.state, fail]);
  const ticketLink = publicUrl(slug, `/ticket/${r.token}`);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ticketLink);
      toast(t('ev.link_copied'));
    } catch {
      // The link stays on screen to copy by hand.
    }
  };
  const save = () =>
    void run(
      () =>
        action('events.update_registration', {
          id: r.id,
          name: f.name.trim(),
          email: f.email.trim() || null,
          phone: f.phone.trim() || null,
          company: f.company.trim() || null,
          note: f.note.trim() || null,
        }).then(onChange),
      t('ev.saved'),
    );
  return (
    <Dialog
      title={r.name}
      onClose={onClose}
      footer={
        edit ? (
          <>
            <Button variant="ghost" onClick={() => setEdit(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" icon="check" busy={busy} onClick={save}>
              {t('common.save')}
            </Button>
          </>
        ) : (
          <>
            {e.can.register && r.state !== 'cancelled' ? (
              <Button variant="ghost" icon="edit" onClick={() => setEdit(true)}>
                {t('ev.edit_details')}
              </Button>
            ) : null}
            {e.can.check_in && r.state === 'confirmed' && e.state === 'published' ? (
              r.checked_in_at ? (
                <Button variant="ghost" icon="undo" busy={busy} onClick={() => void run(() => action('events.undo_check_in', { id: r.id }).then(onChange), t('ev.check_in_undone'))}>
                  {t('ev.undo_check_in')}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  icon="badgeCheck"
                  busy={busy}
                  onClick={() => void run(() => action('events.check_in', { event: e.id, id: r.id }).then(onChange), t('ev.checked_in_done', { name: r.name }))}
                >
                  {t('ev.check_in')}
                </Button>
              )
            ) : null}
            {e.can.register && r.state === 'waiting' && e.state === 'published' ? (
              <Button variant="primary" icon="checkCircle" busy={busy} onClick={() => void run(() => action('events.confirm', { ids: [r.id] }).then(onChange), t('ev.confirmed_done'))}>
                {t('ev.give_seat')}
              </Button>
            ) : null}
          </>
        )
      }
    >
      {edit ? (
        <div className="form-stack">
          <Field label={t('ev.f_name')} required>
            <input className="input" value={f.name} onChange={(x) => setF({ ...f, name: x.target.value })} maxLength={80} />
          </Field>
          <div className="ev-two">
            <Field label={t('ev.f_email')}>
              <input className="input" type="email" value={f.email} onChange={(x) => setF({ ...f, email: x.target.value })} maxLength={160} />
            </Field>
            <Field label={t('ev.f_phone')}>
              <input className="input" type="tel" value={f.phone} onChange={(x) => setF({ ...f, phone: x.target.value })} maxLength={40} />
            </Field>
          </div>
          <Field label={t('ev.f_company')}>
            <input className="input" value={f.company} onChange={(x) => setF({ ...f, company: x.target.value })} maxLength={120} />
          </Field>
          <Field label={t('ev.f_note')}>
            <textarea className="input textarea" rows={2} value={f.note} onChange={(x) => setF({ ...f, note: x.target.value })} maxLength={600} />
          </Field>
        </div>
      ) : (
        <div className="ev-reg">
          <div className="ev-reg-top">
            <RegBadge r={r} />
            <PayBadge r={r} />
          </div>
          <dl className="ev-dl">
            <dt>{t('ev.ref')}</dt>
            <dd className="mono">{r.ref}</dd>
            <dt>{t('ev.ticket_kind')}</dt>
            <dd>
              {r.ticket_name} · {priceText(r.price)}
            </dd>
            {r.email ? (
              <>
                <dt>{t('ev.f_email')}</dt>
                <dd>
                  <a href={`mailto:${r.email}`}>{r.email}</a>
                </dd>
              </>
            ) : null}
            {r.phone ? (
              <>
                <dt>{t('ev.f_phone')}</dt>
                <dd>
                  <a href={`tel:${r.phone.replace(/\s/g, '')}`}>{r.phone}</a>
                </dd>
              </>
            ) : null}
            {r.company ? (
              <>
                <dt>{t('ev.f_company')}</dt>
                <dd>{r.company}</dd>
              </>
            ) : null}
            {r.note ? (
              <>
                <dt>{t('ev.f_note')}</dt>
                <dd>{r.note}</dd>
              </>
            ) : null}
            <dt>{t('ev.booked_at')}</dt>
            <dd>
              {dateTime(r.created_at)} · {r.source === 'web' ? t('ev.booked_online') : t('ev.booked_desk')}
            </dd>
            {r.paid_at ? (
              <>
                <dt>{t('ev.payment')}</dt>
                <dd>{t('ev.paid_line', { at: dateTime(r.paid_at), by: r.paid_by_name ?? '—' })}</dd>
              </>
            ) : null}
            {r.checked_in_at ? (
              <>
                <dt>{t('ev.checked_in')}</dt>
                <dd>{t('ev.checked_in_line', { at: dateTime(r.checked_in_at), by: r.checked_in_name ?? '—' })}</dd>
              </>
            ) : null}
          </dl>
          {r.state === 'confirmed' && r.payment === 'to_pay' && e.can.register ? (
            paying ? (
              <div className="ev-paynow">
                <MethodPick value={method} onChange={setMethod} />
                <Button variant="primary" icon="cash" busy={busy} onClick={() => void run(() => action('events.pay', { ids: [r.id], method }).then(onChange), t('ev.paid_done'))}>
                  {t('ev.mark_paid_amount', { amount: money(r.price) })}
                </Button>
              </div>
            ) : (
              <Button variant="secondary" icon="cash" onClick={() => setPaying(true)}>
                {t('ev.take_payment')}
              </Button>
            )
          ) : null}
          {r.payment === 'paid' && e.can.publish && r.state === 'confirmed' ? (
            <Button variant="ghost" size="sm" icon="undo" busy={busy} onClick={() => void run(() => action('events.pay', { ids: [r.id], method: null }).then(onChange), t('ev.payment_undone'))}>
              {t('ev.undo_payment')}
            </Button>
          ) : null}
          {qr ? (
            <div className="ev-reg-qr">
              <QrCode rows={qr} label={t('ev.qr_label', { code: r.code })} />
              <div className="ev-reg-qr-text">
                <span className="mono ev-code">{r.code.replace(/(.{4})/, '$1 ')}</span>
                <span className="muted small">{t('ev.ticket_link_note')}</span>
                <span className="ev-link-row">
                  <a href={ticketLink} target="_blank" rel="noopener noreferrer" className="small">
                    {t('ev.open_ticket')}
                  </a>
                  <Button size="sm" variant="ghost" icon="copy" onClick={() => void copy()}>
                    {t('ev.copy_link')}
                  </Button>
                </span>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

// ---- the door ----------------------------------------------------------------------------

function Door({ e, onChange }: { e: EventFull; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<{ counts: Counts; recent: Reg[] }>('events.door', { event: e.id });
  const [code, setCode] = useState('');
  const [last, setLast] = useState<Scan | null>(null);
  const [camera, setCamera] = useState(false);
  const [busy, setBusy] = useState(false);
  const { fail, toast } = useUi();
  const input = useRef<HTMLInputElement>(null);
  const check = async (value: string) => {
    const c = value.trim();
    if (!c || busy) return;
    setBusy(true);
    try {
      const r = await action<Scan>('events.check_in', { event: e.id, code: c });
      setLast(r);
      setCode('');
      if (r.result === 'ok' && 'vibrate' in navigator) navigator.vibrate?.(60);
      await reload();
      if (r.result === 'ok') void onChange();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
      input.current?.focus();
    }
  };
  const undo = async (id: string) => {
    try {
      await action('events.undo_check_in', { id });
      toast(t('ev.check_in_undone'));
      setLast(null);
      await reload();
      void onChange();
    } catch (err) {
      fail(err);
    }
  };
  const c = data?.counts ?? e.counts;
  const pct = c.confirmed ? Math.round((c.checked_in / c.confirmed) * 100) : 0;
  return (
    <div className="ev-door">
      <section className="panel ev-door-scan">
        <form
          className="ev-door-form"
          onSubmit={(x) => {
            x.preventDefault();
            void check(code);
          }}
        >
          <Field label={t('ev.scan_label')} hint={t('ev.scan_hint')}>
            <input
              className="input ev-door-input mono"
              ref={input}
              value={code}
              onChange={(x) => setCode(x.target.value)}
              autoFocus
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              inputMode="text"
            />
          </Field>
          <div className="ev-door-buttons">
            <Button type="submit" variant="primary" size="lg" icon="badgeCheck" busy={busy} disabled={!code.trim()}>
              {t('ev.check_in')}
            </Button>
            {hasDetector() ? (
              <Button variant="secondary" size="lg" icon="camera" onClick={() => setCamera(!camera)} aria-pressed={camera}>
                {camera ? t('ev.camera_off') : t('ev.camera')}
              </Button>
            ) : null}
          </div>
        </form>
        {camera ? <CameraScan onCode={(x) => void check(x)} onClose={() => setCamera(false)} /> : null}
        {last ? <ScanResult s={last} onUndo={(id) => void undo(id)} /> : null}
      </section>
      <aside className="ev-door-side">
        <section className="panel ev-door-count" aria-live="polite">
          <span className="muted small">{t('ev.inside')}</span>
          <strong className="ev-door-big">
            {c.checked_in}
            <span className="muted"> / {c.confirmed}</span>
          </strong>
          <span className="ev-fill-bar" aria-hidden="true">
            <span className="ev-fill-in" style={{ width: `${pct}%` }} />
          </span>
          <span className="muted small">{tn('ev.n_still_to_come', Math.max(0, c.confirmed - c.checked_in))}</span>
        </section>
        <section className="panel">
          <div className="panel-head">
            <h2>{t('ev.recent')}</h2>
          </div>
          {data?.recent.length ? (
            <ul className="ev-recent">
              {data.recent.map((r) => (
                <li key={r.id}>
                  <span>
                    <strong>{r.name}</strong>
                    <span className="muted small">
                      {r.ticket_name} · {r.checked_in_at ? since(r.checked_in_at) : ''}
                    </span>
                  </span>
                  <IconButton icon="undo" label={t('ev.undo_for', { name: r.name })} onClick={() => void undo(r.id)} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t('ev.nobody_yet')}</p>
          )}
        </section>
      </aside>
    </div>
  );
}

function ScanResult({ s, onUndo }: { s: Scan; onUndo: (id: string) => void }) {
  const r = s.registration;
  const tone = s.result === 'ok' ? 'ok' : s.result === 'already' || s.result === 'waiting' ? 'warn' : 'danger';
  const icon: IconName = s.result === 'ok' ? 'checkCircle' : s.result === 'unknown' ? 'search' : 'alert';
  return (
    <div className={`ev-scan ev-scan-${tone}`} role="status" aria-live="assertive">
      <Icon name={icon} size={36} />
      <div className="ev-scan-text">
        <strong className="ev-scan-title">{t(`ev.scan_${s.result}` as MessageKey, { name: r?.name ?? '', event: s.other_event ?? '' })}</strong>
        {r ? (
          <span>
            {r.ticket_name}
            {r.company ? ` · ${r.company}` : ''}
            {s.result === 'already' && r.checked_in_at ? ` · ${t('ev.already_at', { at: since(r.checked_in_at) })}` : ''}
          </span>
        ) : null}
        {r && r.state === 'confirmed' && r.payment === 'to_pay' ? (
          <span className="ev-scan-pay">
            <Icon name="cash" size={16} /> {t('ev.collect', { amount: money(r.price) })}
          </span>
        ) : null}
      </div>
      {s.result === 'ok' && r ? (
        <Button variant="ghost" size="sm" icon="undo" onClick={() => onUndo(r.id)}>
          {t('ev.undo')}
        </Button>
      ) : null}
    </div>
  );
}

// ---- details -----------------------------------------------------------------------------

function Details({ e }: { e: EventFull }) {
  return (
    <section className="panel">
      {e.summary ? <p>{e.summary}</p> : null}
      {e.description ? <p className="ev-pre">{e.description}</p> : null}
      <ul className="ev-tickets-view">
        {e.tickets.map((k) => (
          <li key={k.id}>
            <strong>{k.name}</strong> · {priceText(k.price)} · {k.left === null ? tn('ev.n_booked', k.taken) : t('ev.taken_of', { taken: k.taken, capacity: k.taken + k.left })}
          </li>
        ))}
      </ul>
    </section>
  );
}

interface TicketDraft {
  id: string | null;
  key: string;
  name: string;
  description: string;
  price: string;
  seats: string;
}
const priceInput = (minor: number) => (minor ? (minor % 100 ? (minor / 100).toFixed(2) : String(minor / 100)) : '0');

function Editor({ slug, event: e, onSaved, onCancel }: { slug: string; event: EventFull | null; onSaved: (id: string) => void; onCancel: () => void }) {
  const { fail, toast } = useUi();
  const { busy, run } = useSave();
  const start = new Date(Date.now() + 7 * 86_400_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  const defStart = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T19:00`;
  const [f, setF] = useState(() => ({
    name: e?.name ?? '',
    slug: e?.slug ?? '',
    slugTouched: !!e,
    summary: e?.summary ?? '',
    description: e?.description ?? '',
    venue: e?.venue ?? '',
    address: e?.address ?? '',
    online_url: e?.online_url ?? '',
    starts: e?.starts_local.slice(0, 16) ?? defStart,
    ends: e?.ends_local.slice(0, 16) ?? `${defStart.slice(0, 11)}22:00`,
    capacity: e?.capacity ? String(e.capacity) : '',
    waitlist: e?.waitlist ?? true,
    registration_open: e?.registration_open ?? true,
    closes: e?.closes_local?.slice(0, 16) ?? '',
    contact_email: e?.contact_email ?? '',
    contact_phone: e?.contact_phone ?? '',
    tags: (e?.tags ?? []).join(', '),
    cover: e?.cover ?? null,
    cover_version: e?.cover_version ?? null,
  }));
  const [tickets, setTickets] = useState<TicketDraft[]>(() =>
    e?.tickets.length
      ? e.tickets.map((k) => ({ id: k.id, key: k.id, name: k.name, description: k.description ?? '', price: priceInput(k.price), seats: k.seats ? String(k.seats) : '' }))
      : [{ id: null, key: 'new-0', name: t('ev.default_ticket'), description: '', price: '0', seats: '' }],
  );
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const upd = (patch: Partial<typeof f>) => {
    setF((x) => ({ ...x, ...patch }));
    setDirty(true);
  };
  const updT = (key: string, patch: Partial<TicketDraft>) => {
    setTickets((x) => x.map((k) => (k.key === key ? { ...k, ...patch } : k)));
    setDirty(true);
  };
  const upload = async (file: File) => {
    setUploading(true);
    try {
      let shrunk = await shrinkPhoto(file, 1600, 0.82);
      if (shrunk.base64.length * 0.75 > MAX_IMAGE) shrunk = await shrinkPhoto(file, 1200, 0.72);
      const out = await action<{ id: string; version: string }>('events.upload_image', { name: file.name.slice(0, 120) || 'Picture', data: shrunk.base64 });
      upd({ cover: out.id, cover_version: out.version });
      toast(t('ev.picture_added'));
    } catch (err) {
      fail(err instanceof Error && err.message === 'not_image' ? new Error(t('ev.not_a_picture')) : err);
    } finally {
      setUploading(false);
    }
  };
  const badPrice = tickets.some((k) => parseMoney(k.price) === null);
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    void run(async () => {
      const out = await action<{ id: string }>('events.save', {
        id: e?.id ?? null,
        name: f.name.trim(),
        slug: f.slug,
        summary: f.summary.trim() || null,
        description: f.description.trim() || null,
        cover: f.cover,
        venue: f.venue.trim() || null,
        address: f.address.trim() || null,
        online_url: f.online_url.trim() || null,
        starts: f.starts,
        ends: f.ends,
        capacity: f.capacity ? Number(f.capacity) : null,
        waitlist: f.waitlist,
        registration_open: f.registration_open,
        closes: f.closes || null,
        contact_email: f.contact_email.trim() || null,
        contact_phone: f.contact_phone.trim() || null,
        tags: f.tags
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean),
        tickets: tickets.map((k) => ({ id: k.id, name: k.name.trim(), description: k.description.trim() || null, price: parseMoney(k.price) ?? 0, seats: k.seats ? Number(k.seats) : null })),
      });
      setDirty(false);
      onSaved(out.id);
    }, t('ev.saved'));
  };
  return (
    <form className="ev-editor" onSubmit={submit}>
      <section className="panel form-stack">
        <h2 className="ev-h2">{t('ev.about')}</h2>
        <Field label={t('ev.f_title')} required>
          <input className="input" value={f.name} onChange={(x) => upd({ name: x.target.value, ...(f.slugTouched ? {} : { slug: slugify(x.target.value) }) })} maxLength={120} autoFocus={!e} />
        </Field>
        <Field label={t('ev.f_slug')} hint={publicUrl(slug, `/${f.slug || '…'}`)}>
          <input className="input mono" value={f.slug} onChange={(x) => upd({ slug: slugify(x.target.value), slugTouched: true })} maxLength={60} />
        </Field>
        <Field label={t('ev.f_summary')} hint={t('ev.f_summary_hint')} counter={`${f.summary.length}/300`}>
          <input className="input" value={f.summary} onChange={(x) => upd({ summary: x.target.value })} maxLength={300} />
        </Field>
        <Field label={t('ev.f_description')} hint={t('ev.f_description_hint')}>
          <textarea className="input textarea" rows={6} value={f.description} onChange={(x) => upd({ description: x.target.value })} maxLength={8000} />
        </Field>
        <div className="ev-cover-row">
          {f.cover ? (
            <img className="ev-cover" src={coverUrl(slug, f.cover, f.cover_version)} alt={t('ev.cover')} />
          ) : (
            <span className="ev-cover ev-thumb-empty">
              <Icon name="image" size={28} />
            </span>
          )}
          <div className="ev-cover-actions">
            <strong>{t('ev.cover')}</strong>
            <span className="muted small">{t('ev.cover_hint')}</span>
            <span className="ev-link-row">
              <Button size="sm" variant="secondary" icon="upload" busy={uploading} onClick={() => fileRef.current?.click()}>
                {f.cover ? t('ev.change_picture') : t('ev.add_picture')}
              </Button>
              {f.cover ? (
                <Button size="sm" variant="ghost" icon="x" onClick={() => upd({ cover: null, cover_version: null })}>
                  {t('ev.remove_picture')}
                </Button>
              ) : null}
            </span>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(x) => {
                const file = x.target.files?.[0];
                x.target.value = '';
                if (file) void upload(file);
              }}
            />
          </div>
        </div>
      </section>
      <section className="panel form-stack">
        <h2 className="ev-h2">{t('ev.when_where')}</h2>
        <div className="ev-two">
          <Field label={t('ev.f_starts')} required>
            <input
              className="input"
              type="datetime-local"
              value={f.starts}
              onChange={(x) => upd({ starts: x.target.value, ...(x.target.value > f.ends ? { ends: `${x.target.value.slice(0, 11)}${f.ends.slice(11) || '23:00'}` } : {}) })}
            />
          </Field>
          <Field label={t('ev.f_ends')} required>
            <input className="input" type="datetime-local" value={f.ends} min={f.starts} onChange={(x) => upd({ ends: x.target.value })} />
          </Field>
        </div>
        <div className="ev-two">
          <Field label={t('ev.f_venue')} hint={t('ev.f_venue_hint')}>
            <input className="input" value={f.venue} onChange={(x) => upd({ venue: x.target.value })} maxLength={120} />
          </Field>
          <Field label={t('ev.f_address')}>
            <input className="input" value={f.address} onChange={(x) => upd({ address: x.target.value })} maxLength={200} />
          </Field>
        </div>
        <Field label={t('ev.f_online')} hint={t('ev.f_online_hint')}>
          <input className="input" type="url" value={f.online_url} onChange={(x) => upd({ online_url: x.target.value })} maxLength={300} placeholder="https://" />
        </Field>
        <div className="ev-two">
          <Field label={t('ev.f_contact_email')}>
            <input className="input" type="email" value={f.contact_email} onChange={(x) => upd({ contact_email: x.target.value })} maxLength={160} />
          </Field>
          <Field label={t('ev.f_contact_phone')}>
            <input className="input" type="tel" value={f.contact_phone} onChange={(x) => upd({ contact_phone: x.target.value })} maxLength={40} />
          </Field>
        </div>
        <Field label={t('ev.f_tags')} hint={t('ev.f_tags_hint')}>
          <input className="input" value={f.tags} onChange={(x) => upd({ tags: x.target.value })} maxLength={300} />
        </Field>
      </section>
      <section className="panel form-stack">
        <h2 className="ev-h2">{t('ev.seats_bookings')}</h2>
        <div className="ev-two">
          <Field label={t('ev.f_capacity')} hint={t('ev.f_capacity_hint')}>
            <input className="input" type="number" min={1} max={100000} inputMode="numeric" value={f.capacity} onChange={(x) => upd({ capacity: x.target.value })} />
          </Field>
          <Field label={t('ev.f_closes')} hint={t('ev.f_closes_hint')}>
            <input className="input" type="datetime-local" value={f.closes} max={f.ends} onChange={(x) => upd({ closes: x.target.value })} />
          </Field>
        </div>
        <div className="setting-row">
          <div>
            <strong>{t('ev.f_open')}</strong>
            <p className="muted">{t('ev.f_open_hint')}</p>
          </div>
          <Switch checked={f.registration_open} onChange={(v) => upd({ registration_open: v })} label={t('ev.f_open')} />
        </div>
        <div className="setting-row">
          <div>
            <strong>{t('ev.f_waitlist')}</strong>
            <p className="muted">{t('ev.f_waitlist_hint')}</p>
          </div>
          <Switch checked={f.waitlist} onChange={(v) => upd({ waitlist: v })} label={t('ev.f_waitlist')} />
        </div>
        <h3 className="ev-h3">{t('ev.ticket_kinds')}</h3>
        <ul className="ev-kinds">
          {tickets.map((k, i) => {
            const sold = e?.tickets.find((x) => x.id === k.id)?.taken ?? 0;
            return (
              <li key={k.key} className="ev-kind">
                <div className="ev-kind-fields">
                  <Field label={t('ev.k_name')} required>
                    <input className="input" value={k.name} onChange={(x) => updT(k.key, { name: x.target.value })} maxLength={80} />
                  </Field>
                  <Field label={t('ev.k_price')} hint={t('ev.k_price_hint')} error={parseMoney(k.price) === null ? t('ev.bad_price') : null}>
                    <input className="input" value={k.price} onChange={(x) => updT(k.key, { price: x.target.value })} inputMode="decimal" />
                  </Field>
                  <Field label={t('ev.k_seats')} hint={t('ev.k_seats_hint')}>
                    <input className="input" type="number" min={1} inputMode="numeric" value={k.seats} onChange={(x) => updT(k.key, { seats: x.target.value })} />
                  </Field>
                  <Field label={t('ev.k_description')}>
                    <input className="input" value={k.description} onChange={(x) => updT(k.key, { description: x.target.value })} maxLength={200} />
                  </Field>
                </div>
                <div className="ev-kind-end">
                  {sold ? <span className="muted small">{tn('ev.n_booked', sold)}</span> : null}
                  {tickets.length > 1 && !sold ? (
                    <IconButton
                      icon="trash"
                      label={t('ev.remove_kind', { name: k.name || String(i + 1) })}
                      onClick={() => {
                        setTickets(tickets.filter((x) => x.key !== k.key));
                        setDirty(true);
                      }}
                    />
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
        {tickets.length < 12 ? (
          <Button
            size="sm"
            variant="ghost"
            icon="plus"
            onClick={() => {
              setTickets([...tickets, { id: null, key: `new-${Date.now()}`, name: '', description: '', price: '0', seats: '' }]);
              setDirty(true);
            }}
          >
            {t('ev.add_kind')}
          </Button>
        ) : null}
        <p className="muted small">{t('ev.price_note')}</p>
      </section>
      <div className={`ev-save-bar${dirty || !e ? ' show' : ''}`}>
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" icon="check" busy={busy} disabled={badPrice || !f.name.trim() || !f.slug}>
          {e ? t('ev.save_changes') : t('ev.create')}
        </Button>
      </div>
    </form>
  );
}

// ---- settings ----------------------------------------------------------------------------

function SettingsDialog({ initial, slug, onClose, onSaved }: { initial: { public: boolean; intro: string | null }; slug: string; onClose: () => void; onSaved: () => void }) {
  const [pub, setPub] = useState(initial.public);
  const [intro, setIntro] = useState(initial.intro ?? '');
  const { busy, run } = useSave();
  return (
    <Dialog
      title={t('ev.settings')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={() => void run(() => action('events.save_settings', { public: pub, intro: intro.trim() || null }).then(onSaved), t('ev.saved'))}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="setting-row">
          <div>
            <strong>{t('ev.s_public')}</strong>
            <p className="muted">{t('ev.s_public_hint')}</p>
          </div>
          <Switch checked={pub} onChange={setPub} label={t('ev.s_public')} />
        </div>
        {pub ? <p className="muted small mono ev-url">{publicUrl(slug)}</p> : null}
        <Field label={t('ev.s_intro')} hint={t('ev.s_intro_hint')} counter={`${intro.length}/600`}>
          <textarea className="input textarea" rows={3} value={intro} onChange={(x) => setIntro(x.target.value)} maxLength={600} />
        </Field>
      </div>
    </Dialog>
  );
}
